import { handleError, handleSuccess } from '../services/DataHelperFunctions';
import AxiosClient from './AxiosClient';
import { appSettings } from '../settings';
import ApiUrl from '../services/APIUrl';
import { EApiType } from '../data/Constants';

import type {
  IReadPropAssociations,
  IPagination,
  ISavePropAssociations,
  IProductHierarchy,
} from '../../types/IRequestTypes';
import type { INotificationRequest } from '../../types';

export const PdmApi = {
  /**
   * Fetches a paginated list of properties.
   * @param {IPagination} action - Pagination and filter parameters.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  getProperties: (action: IPagination) => {
    return AxiosClient.post(ApiUrl.ReadProperties, action)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return handleError(err);
      });
  },

  /**
   * Fetches property associations based on the provided criteria.
   * @param {IReadPropAssociations} action - Criteria for fetching property associations.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  getPropertyAssociations: (action: IReadPropAssociations) => {
    return AxiosClient.post(ApiUrl.ReadPropertyAssociation, action)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return handleError(err, EApiType.GetPropertyAssociations);
      });
  },

  /**
   * Saves property associations.
   * @param {ISavePropAssociations} action - Data for saving property associations.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  savePropertyAssociations: (action: ISavePropAssociations) => {
    return AxiosClient.post(ApiUrl.CreatePropertyAssociation, action)
      .then((response) => {
        return handleSuccess(response, EApiType.SaveProperties);
      })
      .catch((err) => {
        return handleError(err, EApiType.SaveProperties);
      });
  },

  /**
   * Sends a notification using the notification API.
   * @param {INotificationRequest} action - Notification details.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  sendNotification: (action: INotificationRequest) => {
    return AxiosClient.post(ApiUrl.SendNotification, action)
      .then((response) => {
        return handleSuccess(response, EApiType.SendNotification);
      })
      .catch((err) => {
        return handleError(err, EApiType.SendNotification);
      });
  },

  /**
   * Fetches the list of values (LOV) from the API.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  getListOfValues: () => {
    return AxiosClient.get(ApiUrl.ReadLov, {
      baseURL: appSettings.DisEndpoint,
    })
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return handleError(err);
      });
  },

  /**
   * Fetches the list of product hierarchies.
   * @returns {Promise<any>} Promise resolving to the API response.
   */
  getProductHierarchy: () => {
    const payload: IProductHierarchy = { search: '', agCodeOnly: true };
    return AxiosClient.post(ApiUrl.ReadProductHierarchy, payload, {
      baseURL: appSettings.DisEndpoint,
    })
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return handleError(err);
      });
  },
};
